/**
 * findGroupForType — resolve a RAO CustomerType to its entity-group key.
 *
 * Lightweight v3 port of the SPFx `entityGroupConfig.findGroupForType()`.
 * Keeps groups aligned with the 5 categories documented in
 * docs/port-design/people-form-port.md §9 — Personal, Business, Trust,
 * Estate (separated from Trust for distinct CIP source per §13d), Fiduciary,
 * Government & Special.
 *
 * Returns the group key, or undefined when the customerType is unresolved
 * (e.g., blank-slate new person before a type has been picked).
 */

import type { CustomerType } from '@retail-ops/shared-types/enums';

export type EntityGroupKey =
  | 'personal'
  | 'soleProp'
  | 'business'
  | 'trust'
  | 'estate'
  | 'specialPurpose'
  | 'fiduciary'
  | 'government';

/**
 * Map every customerType value this app may see to its group. Covers both the
 * `CustomerType` enum from shared-types and RAO-local aliases used in the
 * legacy PersonModal's CUSTOMER_TYPES list (`Corporation`, `SoleProp`, `UGMA`,
 * `Conservatorship`, `PublicFund`, `IRA`, `Other`).
 */
const GROUP_BY_TYPE: Record<string, EntityGroupKey> = {
  // Personal
  Individual: 'personal',
  Minor: 'personal',
  NonCitizen: 'personal',
  UTMA: 'personal',
  UGMA: 'personal',
  IRA: 'personal',

  // Sole Proprietor — Personal-with-DBA-overlay cohort. Split from Business
  // per memory `project_business_procedures_full_lock_20260506` — Sole Prop
  // is structurally a Person (one CIF, owner's SSN, Field 39=Y) with a DBA
  // overlay on Account Field 84, NOT a Business entity.
  SoleProp: 'soleProp',
  // DBA legacy alias — also routes to the soleProp wizard since DBA-only
  // accounts use the same Personal-with-DBA-overlay shape.
  DBA: 'soleProp',

  // Business (true business entities — separate legal personality)
  Corp: 'business',
  Corporation: 'business',
  LLC: 'business',
  Partnership: 'business',
  NonProfit: 'business',
  UnincorpAssoc: 'business',
  Political: 'business',

  // Trust — agreement-date CIP (§13c)
  Trust: 'trust',
  RevocableTrust: 'trust',
  IrrevocableTrust: 'trust',

  // Estate — court-case CIP (§13d); separated from Trust group for distinct CIP source
  Estate: 'estate',

  // Special Purpose — narrow restricted-use cohorts per 7-bucket lock
  // (project_entity_groups_7bucket_lock_20260505 + project_snt_moved_to_special_
  // purpose_20260511). QIT/SNT are structurally trusts; IOLTA + ICS_Business
  // are EIN-bearing trust-shaped accounts; MSA/USDAGrant/ICS_Personal are
  // restricted-use personal-shaped accounts. All route through
  // SpecialPurposePersonDialog, which currently defers to TrustPersonDialog
  // as a stub until per-subtype curated flows land.
  QIT: 'specialPurpose',
  SelfSettledSNT: 'specialPurpose',
  IOLTA: 'specialPurpose',
  MedicalSetAside: 'specialPurpose',
  USDAGrant: 'specialPurpose',
  ICS_Business: 'specialPurpose',
  ICS_Personal: 'specialPurpose',

  // Fiduciary
  RepPayee: 'fiduciary',
  Conservator: 'fiduciary',
  Conservatorship: 'fiduciary',
  VAFiduciary: 'fiduciary',

  // Government & Special
  PublicFund: 'government',
  PublicFunds: 'government',
  Donation: 'government',
};

export function findGroupForType(
  customerType: CustomerType | 'Minor' | string | undefined | null,
): EntityGroupKey | undefined {
  if (!customerType) return undefined;
  return GROUP_BY_TYPE[String(customerType)];
}
